/**
 * Silk.Shuttle Backstage theme — single source of truth for all visual styling.
 *
 * WHAT THIS FILE REPLACES (deviations from Backstage defaults)
 * ─────────────────────────────────────────────────────────────
 * Out of the box, Backstage ships with a light theme and a dark theme, both
 * using teal/blue as the primary color, Roboto as the font, and a gray/white
 * canvas. This file replaces ALL of that with the Silk brand:
 *
 *   Default → Silk.Shuttle
 *   ──────────────────────
 *   Teal primary (#7C3AED / #9BF0E1)  → Fuchsia #FF286E
 *   White/gray canvas                  → Navy #0F0721
 *   White/gray cards (paper)           → Navy #0A1128
 *   Teal page banner gradient          → Fuchsia-to-navy gradient
 *   Teal sidebar indicator             → Fuchsia #FF286E
 *   Roboto font                        → Open Sans (loaded in public/index.html)
 *   Default BUI top bar (gray/white)   → Navy #0A1128 via CSS variable overrides
 *   "Create" link (BUI default color)  → Fuchsia #FF286E
 *   Filter panel labels (mixed styles) → Unified uppercase, 11px, #FF286E on active
 *   No root "/" route (404 on landing) → Redirects to /create (see modules/home)
 *   Both light + dark themes available → Only Silk.Shuttle (disabled in app-config.yaml)
 *   Template card banners (teal)       → Fuchsia-to-navy (requires pageTheme entries
 *                                        for each silk-* template type)
 *
 * ARCHITECTURE OVERVIEW
 * ─────────────────────
 * Backstage runs MUI v4 (@material-ui/core 4.x). MUI v4 does NOT support the
 * string-body form of styleOverrides (that's MUI v5 only). The correct MUI v4
 * approach for global CSS injection is JSS makeStyles with the '@global' key.
 *
 * Styling is split across three layers — use the right layer for each concern:
 *
 *  1. useGlobalStyles / GlobalStyles (JSS @global, rendered inside the Provider)
 *     Use for: CSS that targets generated class names (e.g. .MuiTab-root),
 *     BUI CSS variable overrides ([data-unified-theme-stack]), and any selector
 *     that needs to pierce MUI's scoped styles with !important.
 *     Controls: top bar (BUI vars), tab bar, filter panel labels, filter list
 *               items, dropdowns, font family overrides.
 *
 *  2. createUnifiedTheme → palette / pageTheme / components
 *     Use for: token-level decisions that MUI components read natively —
 *     palette colors, navigation bar colors, page banner gradients, per-component
 *     styleOverrides that don't need !important.
 *     Controls: sidebar background + indicator, page banner gradient, paper
 *               background, MuiTypography heading colors, MuiChip colors.
 *
 *  3. Individual component files (SidebarLogo, LogoFull, SilkAttribution, etc.)
 *     Use for: layout and sizing of custom components that live outside MUI's
 *     component library. Theme palette is not the right place for pixel-level
 *     sizing of our custom sidebar logo or attribution block.
 *
 * COLOR SCHEME
 * ────────────
 * The palette is a dark space/navy base with Silk's fuchsia brand color as the
 * single high-contrast accent. The intent is: navy recedes, fuchsia draws the
 * eye to everything that matters — actions, active state, brand identity.
 *
 * Three navy tones create depth without introducing new hues:
 *   #0F0721  canvas    — the darkest layer; the "void" behind everything.
 *                        Sampled from the starfield in shuttle.png so the
 *                        rocket bleeds into the sidebar edge seamlessly.
 *   #0A1128  surface   — cards, drawers, the top bar, page header banner.
 *                        One step lighter than canvas to lift content off the bg.
 *   #1C2B4A  mid       — input/dropdown backgrounds. Visibly interactive vs surface.
 *
 * Two fuchsia tones for the accent:
 *   #FF286E  primary   — every active, selected, branded, or linked element.
 *                        Headings, tags, nav indicator, tab underline, "Create"
 *                        link, selected filter row, focus ring, links.
 *   #FF5A8E  light     — hover state only. Lightens the primary on mouse-over
 *                        to give tactile feedback without introducing a new hue.
 *
 * Text follows a three-tier opacity hierarchy on white:
 *   #FFFFFF / 100%      — primary content (list items, nav labels, body text)
 *   rgba(255,255,255,0.6–0.85) — secondary content (subtitles, section labels)
 *   rgba(255,255,255,0.4–0.45) — tertiary / dimmed (counts, version stamp,
 *                                 inactive input labels)
 *
 * Font: Open Sans 400/600/700 — loaded via Google Fonts in public/index.html.
 *   400 body, 600 nav/tab labels, 700 headings and filter section labels.
 */

import type { PropsWithChildren } from 'react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { ThemeBlueprint } from '@backstage/plugin-app-react';
// eslint-disable-next-line @backstage/no-undeclared-imports
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { makeStyles } from '@material-ui/core';
import { themeForcerExtension } from './forcer';

// ─── LAYER 1: Global CSS overrides (JSS @global) ────────────────────────────
//
// Anything here beats MUI's scoped styles because it's injected as a plain
// <style> tag with !important. Target generated class-name patterns rather
// than hardcoded hashes so it survives Backstage upgrades.
//
// Sections:
//   BUI CSS variables  — controls the PluginHeader top bar background + text
//   Font               — ensures Open Sans is applied everywhere MUI might override
//   Canvas             — dark navy base for body / #root / main
//   Page header        — BackstageHeader banner (the gradient strip under tabs)
//   Tab bar            — MuiTab colors and indicator
//   Filter labels      — PERSONAL/SILK (overline), CATEGORIES/OWNER (button typography)
//   Filter list items  — catalog/scaffolder sidebar filter rows
//   Dropdowns          — MuiOutlinedInput / MuiSelect styling
const useGlobalStyles = makeStyles({
  '@global': {
    // ── BUI CSS variables ──────────────────────────────────────────────────
    // Backstage's new BUI (Backstage UI) design system uses CSS custom
    // properties on [data-unified-theme-stack] instead of MUI palette keys.
    // These control the PluginHeader top bar background, "Create" link color,
    // and other BUI-rendered chrome that doesn't read the MUI palette.
    '[data-unified-theme-stack]': {
      '--bui-bg-app': '#0F0721 !important' as any, // page/app chrome background
      '--bui-bg-neutral-1': '#0A1128 !important' as any, // top bar surface
      '--bui-bg-neutral-2': '#1C2B4A !important' as any, // secondary surface
      '--bui-fg-primary': '#FF286E !important' as any, // "Create" link + primary icons
      '--bui-fg-secondary': 'rgba(255,255,255,0.6) !important' as any,
      '--bui-color-primary': '#FF286E !important' as any, // active/focus accent
    },

    // ── Atlas title bar — the ikat weave ───────────────────────────────────
    // This is the bar reading e.g. "Silk GTM Demo Infrastructure" above the docs,
    // with the docs search on the right.
    //
    // It is a Backstage UI component - div.bui-Header - NOT the MUI
    // BackstageHeader, and NOT mkdocs' .md-header. Both of those were tried and
    // neither renders on this page:
    //   - TechDocs hides mkdocs' own header and draws its own
    //   - the alpha frontend system uses @backstage/ui here, so pageTheme /
    //     theme.page.backgroundImage is never consulted
    // Confirmed from the live DOM: the element carries bui-Container / bui-Header
    // and its own rule sets only display/flex-direction/gap/margin-top - there is
    // no background property to override, which is why nothing appeared.
    //
    // Scoped with :has() to the header that contains the docs search, so catalog
    // and scaffolder page headers keep the flat treatment. Widen or narrow that
    // selector if other pages pick it up.
    //
    // No !important needed: BUI ships its rules inside @layer, and unlayered
    // declarations beat layered ones regardless of specificity.
    //
    // padding-block gives the bar enough height for the weave to read - the
    // unstyled row is only ~34px tall, which would show a sliver of the tile.
    '.bui-Header:has([class*="SearchAutocompleteSearchField"])': {
      backgroundColor: '#0A1128' as any,
      backgroundImage:
        'linear-gradient(rgba(15, 7, 33, 0.62), rgba(15, 7, 33, 0.62)), url("/silk-atlas.png")' as any,
      backgroundRepeat: 'no-repeat, repeat' as any,
      backgroundSize: '100% 100%, 140px 140px' as any,
      backgroundPosition: 'center, center' as any,
      paddingBlock: '20px' as any,
      paddingInline: '20px' as any,
      borderRadius: '8px' as any,
      boxShadow: 'inset 0 -1px 0 rgba(255, 40, 110, 0.35)' as any,
    },

    // ── "Create" link in PluginHeader top bar ──────────────────────────────
    // The PluginHeader renders anchor tags that read --bui-fg-primary above,
    // but we also add a direct color rule as a fallback for older BUI versions.
    '[class*="PluginHeader"] a, [class*="PluginHeaderToolbarName"] a': {
      color: '#FF286E !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
      fontWeight: '600 !important' as any,
    },

    // ── Font — apply Open Sans everywhere MUI might revert to Roboto ──────
    'body, button, input, select, textarea': {
      fontFamily: '"Open Sans", sans-serif !important' as any,
    },

    // ── Canvas — dark navy base ────────────────────────────────────────────
    'body, #root, main': {
      backgroundColor: '#0F0721 !important' as any,
    },
    '[class*="BackstagePage-root"]': {
      backgroundColor: '#0F0721 !important' as any,
    },

    // ── Page header banner (strip under top bar, above tabs) ──────────────
    // MUI's BackstageHeader renders a gradient by default; flattened to navy
    // with a fuchsia underline.
    //
    // Nothing in this app renders it - every page comes from PageBlueprint and
    // Backstage UI - so this is defensive only. The Atlas weave deliberately
    // does NOT live here: the bui-Header rule above is the element that actually
    // draws the docs title bar. An earlier attempt drove the weave from
    // pageTheme.documentation via this class and rendered nothing, because
    // theme.page.backgroundImage is never consulted by BUI.
    '[class*="BackstageHeader-header"]': {
      backgroundColor: '#0A1128 !important' as any,
      backgroundImage: 'none !important' as any,
      boxShadow: '0 1px 0 rgba(255,40,110,0.4) !important' as any,
    },
    // The "type" chip (e.g. "WEBSITE") inside the page header — match background
    '[class*="BackstageHeader-type"]': {
      color: '#0A1128 !important' as any,
    },

    // ── Tab bar ────────────────────────────────────────────────────────────
    '.MuiTab-root': {
      color: 'rgba(255,255,255,0.5) !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
      fontWeight: '600 !important' as any,
      fontSize: '13px !important' as any,
      letterSpacing: '0.04em !important' as any,
      textTransform: 'none !important' as any,
    },
    '.MuiTab-root.Mui-selected': {
      color: '#FF286E !important' as any,
    },
    '.MuiTabs-indicator': {
      backgroundColor: '#FF286E !important' as any,
    },

    // ── Filter panel — all form/input labels ──────────────────────────────
    // Unified style for any MUI label/form-label so things like "Search" or
    // inline input labels match the filter section headings.
    '.MuiInputLabel-root, .MuiFormLabel-root': {
      fontFamily: '"Open Sans", sans-serif !important' as any,
      color: 'rgba(255,255,255,0.45) !important' as any,
      fontSize: '11px !important' as any,
      fontWeight: '700 !important' as any,
      letterSpacing: '0.1em !important' as any,
      textTransform: 'uppercase !important' as any,
    },
    '.MuiInputLabel-root.Mui-focused, .MuiFormLabel-root.Mui-focused': {
      color: '#FF286E !important' as any,
    },

    // ── Filter section group headings: PERSONAL, SILK ─────────────────────
    // These are rendered as MuiTypography-overline by CatalogReactEntityOwnerPicker
    // and similar catalog-react components.
    '.MuiTypography-overline': {
      fontFamily: '"Open Sans", sans-serif !important' as any,
      color: 'rgba(255,255,255,0.6) !important' as any,
      fontSize: '11px !important' as any,
      fontWeight: '700 !important' as any,
      letterSpacing: '0.14em !important' as any,
    },

    // ── Filter section group headings: CATEGORIES, OWNER ──────────────────
    // CATEGORIES is rendered as MuiTypography-button by ScaffolderReactTemplateCategoryPicker.
    // OWNER is rendered as MuiTypography-button by CatalogReactEntityOwnerPicker.
    // Both are matched here so all four section labels look identical.
    '[class*="ScaffolderReactTemplateCategoryPicker-label"], [class*="CatalogReactEntityOwnerPicker-"] .MuiTypography-root, .MuiTypography-button':
      {
        fontFamily: '"Open Sans", sans-serif !important' as any,
        color: 'rgba(255,255,255,0.6) !important' as any,
        fontSize: '11px !important' as any,
        fontWeight: '700 !important' as any,
        letterSpacing: '0.14em !important' as any,
        textTransform: 'uppercase !important' as any,
      },

    // ── Filter list items (catalog/scaffolder sidebar rows) ────────────────
    '.MuiListItemText-primary': {
      fontFamily: '"Open Sans", sans-serif !important' as any,
      color: '#FFFFFF !important' as any,
      fontSize: '13px !important' as any,
    },
    '.MuiListItemSecondaryAction-root': {
      color: 'rgba(255,255,255,0.4) !important' as any,
      fontSize: '12px !important' as any,
    },
    // Selected filter item — fuchsia left border + tinted background
    '.MuiListItem-root.Mui-selected': {
      backgroundColor: 'rgba(255,40,110,0.1) !important' as any,
      borderLeft: '2px solid #FF286E !important' as any,
    },
    '.MuiListItem-root.Mui-selected .MuiListItemText-primary': {
      color: '#FF286E !important' as any,
    },

    // ── Dropdowns / select inputs ──────────────────────────────────────────
    // Used by CATEGORIES and OWNER filter dropdowns in the scaffolder and catalog.
    '.MuiOutlinedInput-root': {
      backgroundColor: '#1C2B4A !important' as any,
      borderRadius: '6px !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
    },
    '.MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.12) !important' as any,
    },
    '.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,40,110,0.4) !important' as any,
    },
    '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF286E !important' as any,
    },
    '.MuiSelect-icon': {
      color: 'rgba(255,255,255,0.4) !important' as any,
    },
    '.MuiSelect-root': {
      color: '#FFFFFF !important' as any,
      fontSize: '14px !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
      fontWeight: '400 !important' as any,
    },
    // Normalize plain text input values to match Select field appearance
    '.MuiInputBase-input': {
      color: '#FFFFFF !important' as any,
      fontSize: '14px !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
      fontWeight: '400 !important' as any,
    },
    // Checkboxes — unchecked border visible, checked state fuchsia
    '.MuiCheckbox-root': {
      color: 'rgba(255,255,255,0.5) !important' as any,
    },
    '.MuiCheckbox-root.Mui-checked': {
      color: '#FF286E !important' as any,
    },
    // Checkbox label text
    '.MuiFormControlLabel-label': {
      color: '#FFFFFF !important' as any,
      fontSize: '14px !important' as any,
      fontFamily: '"Open Sans", sans-serif !important' as any,
    },
  },
});

const GlobalStyles = () => {
  useGlobalStyles();
  return null;
};

// ─── LAYER 2: createUnifiedTheme — palette + pageTheme + components ──────────
//
// These are MUI palette tokens and component-level styleOverrides that MUI
// components read natively (no !important needed). Use this layer for
// anything that doesn't need to fight specificity battles.
//
// pageTheme: maps to the `themeId` passed by Backstage's page chrome. The
// scaffolder TemplateCard calls getPageTheme({ themeId: template.spec.type }),
// so we must register entries for our custom template types (silk-*) in
// addition to Backstage's built-in keys, otherwise cards fall back to teal.

// Banner gradient used for every named pageTheme below.
// Replaces the Backstage default teal on template card headers and page banners.
const brandedPageTheme = genPageTheme({
  colors: ['#FF286E', '#0A1128'],
  shape: shapes.wave,
});

// Exported so the SIGN-IN page can apply it directly. The sign-in page renders
// before the app's theme extensions are live, so it cannot rely on appThemeApi and
// otherwise falls back to Backstage's built-in dark theme - the neutral gray canvas
// and teal page banner that made the login screen look unbranded.
const shuttleTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    // Primary fuchsia — buttons, checkboxes, focus rings
    primary: { main: '#FF286E', contrastText: '#FFFFFF' },
    // Secondary navy — used for contained secondary buttons
    secondary: { main: '#0A1128', contrastText: '#FFFFFF' },
    error: { main: '#FF286E' },
    // Canvas + card surface
    background: {
      default: '#0F0721', // page canvas
      paper: '#0A1128', // cards, drawers, tab bar (BackstageHeaderTabs reads this)
    },
    // Sidebar navigation
    navigation: {
      ...palettes.dark.navigation,
      background: '#0F0721', // sidebar background
      indicator: '#FF286E', // active nav item left indicator bar
      color: '#FFFFFF', // nav item text
      selectedColor: '#FF286E', // active nav item text
      navItem: {
        hoverBackground: 'rgba(255, 40, 110, 0.16)',
      },
    },
    link: '#FF286E',
    linkHover: '#FF5A8E',
    status: {
      ...palettes.dark.status,
      running: '#FF286E',
    },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    // Backstage built-in page theme keys
    home: brandedPageTheme,
    documentation: brandedPageTheme,
    tool: brandedPageTheme,
    service: brandedPageTheme,
    website: brandedPageTheme,
    library: brandedPageTheme,
    other: brandedPageTheme,
    app: brandedPageTheme,
    apis: brandedPageTheme,
    // Silk-specific template types — required so TemplateCard headers use
    // the branded gradient instead of the default teal fallback
    'silk-data-load': brandedPageTheme,
    'silk-echo-workflow-step': brandedPageTheme,
    'silk-echo-workflow': brandedPageTheme,
    'silk-automation': brandedPageTheme,
  },
  components: {
    // Canvas dark background — multiple override targets to beat MUI gray.
    // BackstageContent / BackstagePage are Backstage-specific component keys.
    BackstageContent: {
      styleOverrides: {
        root: { backgroundColor: '#0F0721' },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: { backgroundColor: '#0F0721' },
      },
    },
    // Cards, drawers, menus, popovers
    MuiPaper: {
      styleOverrides: {
        root: { backgroundColor: '#0A1128' },
      },
    },
    // Page/section headings — fuchsia.
    // Body text and subtitles remain white/dimmed-white from palettes.dark.
    MuiTypography: {
      styleOverrides: {
        h1: { color: '#FF286E' },
        h2: { color: '#FF286E' },
        h3: { color: '#FF286E' },
        h4: { color: '#FF286E' },
        h5: { color: '#FF286E' },
        subtitle1: { color: 'rgba(255,255,255,0.85)' },
        subtitle2: { color: 'rgba(255,255,255,0.7)' },
      },
    },
    // Taxonomy tags / entity kind chips — fuchsia bubble with navy text
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF286E',
          color: '#0A1128',
          fontWeight: 700,
          '&:hover': {
            backgroundColor: '#FF5A8E',
          },
        },
        label: {
          color: '#0A1128',
        },
      },
    },
  },
});

// Applies the Silk theme. GlobalStyles is rendered INSIDE UnifiedThemeProvider so the
// JSS @global rules are injected within the same MUI theme context.
//
// Exported because the SIGN-IN page needs it too. That page renders before the app's
// theme extensions are live, so it cannot rely on appThemeApi, and forcer.tsx cannot
// help either - that runs inside AppRootWrapper, which is downstream of sign-in. Left
// alone, Backstage falls back to its built-in dark theme, which is the gray canvas and
// teal banner that made the login screen look unbranded.
//
// Sharing this component rather than exporting the raw theme keeps ONE definition of
// how the Silk theme is applied, so the login screen cannot drift from the app.
export const ShuttleThemeProvider = ({ children }: PropsWithChildren<{}>) => (
  <UnifiedThemeProvider theme={shuttleTheme}>
    <GlobalStyles />
    {children}
  </UnifiedThemeProvider>
);

// ─── Theme extension + module export ─────────────────────────────────────────

const shuttleThemeExtension = ThemeBlueprint.make({
  name: 'shuttle',
  params: {
    theme: {
      id: 'shuttle',
      title: 'Silk.Shuttle',
      variant: 'dark',
      Provider: ShuttleThemeProvider,
    },
  },
});

// themeForcerExtension (./forcer) wraps the app root to auto-activate the
// 'shuttle' theme for guest sessions that haven't chosen a theme yet.
export const themeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [shuttleThemeExtension, themeForcerExtension],
});
